import { addDays, isAfter, startOfDay } from 'date-fns';
import { INFLUENCER_FUTURE_DAYS, isSubscribed } from '@puzzlio/core';
import type { StripeSubscription } from '@/app/actions/stripe';
import type { User } from '@/app/types/user';
import { ResultVoid, SimpleError } from '../types/utils';

const treatAdminsAsSubscribers = true;

/**
 * Returns true if the user holds a subscription that currently grants access.
 *
 * Delegates to core so the website and the app can't disagree about what a subscription entitles
 * someone to — including a failed payment still inside its retry window, which counts.
 */
export function isSubscriber(subscription: StripeSubscription | null | undefined): boolean {
  return isSubscribed(subscription);
}

/** Roles that get a complimentary subscription. */
const COMPED_ROLES: ReadonlySet<User['role']> = new Set(['friend', 'influencer', 'puzzle_creator']);

/**
 * Returns true if the user is an active subscriber, a site admin, or holds a comped role
 * (friend, influencer or puzzle creator). Use this wherever subscribers should get the same access as paid users.
 */
export function hasSubscriberAccess(subscription: StripeSubscription | null | undefined, user: User): boolean {
  return (
    isSubscriber(subscription) || (treatAdminsAsSubscribers && user.role === 'admin') || COMPED_ROLES.has(user.role)
  );
}

/**
 * Returns true if the user can access the given future date.
 * Admins and puzzle creators have unlimited future access; influencers get INFLUENCER_FUTURE_DAYS
 * days ahead.
 */
export function hasFutureAccess(user: User, date: Date): boolean {
  if (user.role === 'admin' || user.role === 'puzzle_creator') return true;
  if (user.role === 'influencer') {
    return !isAfter(startOfDay(date), addDays(startOfDay(new Date()), INFLUENCER_FUTURE_DAYS));
  }
  return false;
}

/**
 * Safely decodes a redirect_to query param.
 * Returns '/' if the value is missing, malformed, or points outside the app.
 */
export function safeDecodeRedirect(raw: string | null | undefined): string {
  if (!raw) return '/';
  try {
    const decoded = decodeURIComponent(raw);
    return decoded.startsWith('/') && !decoded.startsWith('//') ? decoded : '/';
  } catch {
    return '/';
  }
}

export async function handleAuthAction<T extends ResultVoid<SimpleError>>(action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch (_err) {
    return { error: { message: 'Unknown error, please try again later.' } } as T;
  }
}
